import {checkoutAttemptStorageKey} from "@workspace/api-client-react";
import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { CheckCircle2, Heart, ShoppingBag, Circle, Check, Clock, Truck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";

const ACTIVE_STATUSES = new Set(["pending", "confirmed", "preparing"]);

function CountdownTimer({ createdAt, estimatedMinutes, fulfilmentType }: {
  createdAt: string; estimatedMinutes: number; fulfilmentType: string;
}) {
  const readyAt = new Date(createdAt).getTime() + estimatedMinutes * 60_000;
  const [secsLeft, setSecsLeft] = useState(() => Math.max(0, Math.round((readyAt - Date.now()) / 1000)));

  useEffect(() => {
    const id = setInterval(() => setSecsLeft(Math.max(0, Math.round((readyAt - Date.now()) / 1000))), 1000);
    return () => clearInterval(id);
  }, [readyAt]);

  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;
  const isOverdue = secsLeft === 0;
  const label = fulfilmentType === "delivery" ? "delivery" : "collection";

  return (
    <div className={`flex items-center gap-3 rounded-xl px-4 py-3 mb-4 ${isOverdue ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200" : "bg-primary/5 border border-primary/20"}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isOverdue ? "bg-amber-100" : "bg-primary/10"}`}>
        <Clock size={18} className={isOverdue ? "text-amber-600" : "text-primary"} />
      </div>
      <div className="text-left">
        <p className={`text-xs font-semibold ${isOverdue ? "text-amber-700" : "text-primary"}`}>
          {isOverdue ? `Ready for ${label} any moment…` : `Estimated ${label} time`}
        </p>
        {!isOverdue && (
          <p className="text-[34px] leading-tight tracking-[-1.2px] font-bold text-[#1e3a5f] leading-tight tabular-nums">
            {mins}:{String(secs).padStart(2, "0")}
            <span className="text-sm font-normal text-slate-500 ml-1">remaining</span>
          </p>
        )}
        {!isOverdue && (
          <p className="text-xs text-slate-400">approx. {estimatedMinutes} mins total</p>
        )}
      </div>
    </div>
  );
}

const STEPS = [
  { key: "pending",          label: "Order placed" },
  { key: "confirmed",        label: "Confirmed" },
  { key: "preparing",        label: "Preparing" },
  { key: "ready",            label: "Ready" },
  { key: "out_for_delivery", label: "On the way" },
  { key: "delivered",        label: "Delivered" },
] as const;

const COLLECTION_STEPS = [
  { key: "pending",    label: "Order placed" },
  { key: "confirmed",  label: "Confirmed" },
  { key: "preparing",  label: "Preparing" },
  { key: "ready",      label: "Ready for collection" },
  { key: "delivered",  label: "Collected" },
] as const;

type Step = { key: string; label: string };

function stepIndex(steps: readonly Step[], status: string): number {
  const idx = steps.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
}

function StatusTracker({ status, fulfilmentType }: { status: string; fulfilmentType: string }) {
  const steps = fulfilmentType === "delivery" ? STEPS : COLLECTION_STEPS;
  const cancelled = status === "cancelled";
  const current = cancelled ? -1 : stepIndex(steps, status);

  return (
    <div className="w-full mt-6 mb-2">
      {cancelled ? (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center">
          <p className="text-red-700 font-semibold text-sm">Order cancelled</p>
        </div>
      ) : (
        <div className="relative flex flex-col gap-0">
          {steps.map((step, i) => {
            const done = i < current;
            const active = i === current;
            return (
              <div key={step.key} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-colors ${
                    done    ? "bg-emerald-500 border-emerald-500" :
                    active  ? "bg-primary border-primary" :
                              "bg-white border-slate-200"
                  }`}>
                    {done ? (
                      <Check size={14} className="text-white" strokeWidth={3} />
                    ) : active ? (
                      <div className="w-2.5 h-2.5 rounded-full bg-white" />
                    ) : (
                      <Circle size={10} className="text-slate-300" />
                    )}
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`w-0.5 h-6 mt-0.5 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />
                  )}
                </div>
                <div className="pt-1 pb-6">
                  <span className={`text-sm font-medium ${
                    active ? "text-primary" : done ? "text-emerald-700" : "text-slate-400"
                  }`}>
                    {step.label}
                  </span>
                  {active && (
                    <span className="ml-2 inline-block text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium animate-pulse">
                      Now
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function OrderSuccess() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const storeId = params.get("storeId");
  const orderIdParam = params.get("orderId");
  const [orderId, setOrderId] = useState<number | null>(orderIdParam ? Number(orderIdParam) : null);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("pending");
  const [fulfilmentType, setFulfilmentType] = useState("delivery");
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | null>(null);
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  const [kindnessCreditPence, setKindnessCreditPence] = useState(0);
  const [kindnessDonorName, setKindnessDonorName] = useState<string | null>(null);
  const { loginWithToken } = useAuth();

  // Guest registration prompt state
  const [isGuest, setIsGuest] = useState(false);
  const [guestDismissed, setGuestDismissed] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPassword, setGuestPassword] = useState("");
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);
  const [guestDone, setGuestDone] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("ayrshire_is_guest") === "1") setIsGuest(true);
  }, []);

  async function claimGuestAccount() {
    const trimmedEmail = guestEmail.trim().toLowerCase();
    if (!trimmedEmail) { setGuestError("Please enter your email address"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) { setGuestError("Please enter a valid email"); return; }
    if (!guestPassword || guestPassword.length < 8) { setGuestError("Password must be at least 8 characters"); return; }
    setGuestError(null);
    setGuestSubmitting(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const res = await fetch("/api/auth/guest/claim", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ email: trimmedEmail, password: guestPassword }),
      });
      const data = await res.json() as { token?: string; error?: string };
      if (!res.ok) { setGuestError(data.error ?? "Something went wrong, please try again"); return; }
      if (data.token) {
        localStorage.setItem("ayrshire_token", data.token);
        await loginWithToken(data.token);
      }
      localStorage.removeItem("ayrshire_is_guest");
      setGuestDone(true);
    } catch {
      setGuestError("Connection error, please try again");
    } finally {
      setGuestSubmitting(false);
    }
  }

  function dismissGuestPrompt() {
    localStorage.removeItem("ayrshire_is_guest");
    setGuestDismissed(true);
  }

  // Resolve the slug of the store just ordered from so "Order again" returns
  // straight to that shop rather than the generic store list.
  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    fetch(`/api/order/stores/${storeId}`)
      .then((r) => (r.ok ? (r.json() as Promise<{ store?: { slug?: string | null } }>) : null))
      .then((d) => {
        if (!cancelled && d?.store?.slug) setStoreSlug(d.store.slug);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [storeId]);

  useEffect(() => {
    const cash = params.get("cash");
    if (cash === "1") {
      setVerified(true);
      return;
    }
    // Always prefer the Checkout Session ID — Stripe Checkout appends it via the
    // {CHECKOUT_SESSION_ID} placeholder. Fall back to payment_intent only for legacy flows.
    const sessionId = params.get("session_id") ?? params.get("payment_intent");
    if (!sessionId || !storeId) { setVerified(true); return; }
    const token = localStorage.getItem("ayrshire_token");

    async function tryVerify(attemptsLeft: number): Promise<void> {
      try {
        const r = await fetch("/api/order/checkout-verify", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ sessionId, storeId: Number(storeId) }),
        });
        const d = await r.json() as { order?: { id: number; fulfilmentType: string; status: string; createdAt?: string; estimatedMinutes?: number; kindnessCreditPence?: number; kindnessDonorName?: string | null }; processing?: boolean; error?: string };
        if (d.order) {
          try{sessionStorage.removeItem(checkoutAttemptStorageKey(storeId!,"hosted"));}catch{}
          setOrderId(d.order.id);
          setStatus(d.order.status);
          setFulfilmentType(d.order.fulfilmentType);
          if (d.order.createdAt) setCreatedAt(d.order.createdAt);
          if (d.order.estimatedMinutes) setEstimatedMinutes(d.order.estimatedMinutes);
          if (typeof d.order.kindnessCreditPence === "number") setKindnessCreditPence(d.order.kindnessCreditPence);
          setKindnessDonorName(d.order.kindnessDonorName ?? null);
          setVerified(true);
        } else if ((r.status === 202 || d.processing) && attemptsLeft > 0) {
          // Async payment method (e.g. Klarna) — session complete but capture still processing
          await new Promise((res) => setTimeout(res, 4000));
          return tryVerify(attemptsLeft - 1);
        } else if (r.status >= 500 && attemptsLeft > 0) {
          // Server error (e.g. metadata temporarily unavailable) — retry after 5s
          await new Promise((res) => setTimeout(res, 5000));
          return tryVerify(attemptsLeft - 1);
        } else {
          setError(d.error ?? "Could not record your order — please contact the shop.");
          setVerified(true);
        }
      } catch {
        if (attemptsLeft > 0) {
          await new Promise((res) => setTimeout(res, 5000));
          return tryVerify(attemptsLeft - 1);
        }
        setError("Your payment was taken. Your order is being confirmed — please check 'My Orders' in 60 seconds.");
        setVerified(true);
      }
    }
    void tryVerify(6);
  }, []);

  useEffect(() => {
    if (!orderId) return;
    const token = localStorage.getItem("ayrshire_token");
    const TERMINAL = new Set(["delivered", "cancelled"]);
    let stopped = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    function poll() {
      if (stopped) return;
      fetch(`/api/order/orders/${orderId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((r) => {
          if (!r.ok) return;
          return r.json() as Promise<{ status?: string; fulfilmentType?: string; createdAt?: string; estimatedMinutes?: number; kindnessCreditPence?: number; kindnessDonorName?: string | null }>;
        })
        .then((d) => {
          if (!d) return;
          if (d.status) setStatus(d.status);
          if (d.fulfilmentType) setFulfilmentType(d.fulfilmentType);
          if (d.createdAt) setCreatedAt(d.createdAt);
          if (d.estimatedMinutes) setEstimatedMinutes(d.estimatedMinutes);
          if (typeof d.kindnessCreditPence === "number") setKindnessCreditPence(d.kindnessCreditPence);
          if (d.kindnessDonorName !== undefined) setKindnessDonorName(d.kindnessDonorName ?? null);
          if (d.status && TERMINAL.has(d.status)) {
            stopped = true;
            if (interval) clearInterval(interval);
          }
        })
        .catch(() => {});
    }
    poll();
    interval = setInterval(poll, 5000);
    return () => { stopped = true; if (interval) clearInterval(interval); };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-white px-5 py-10">
      <div className="bg-white max-w-lg mx-auto w-full text-left">
        <div className="w-20 h-20 bg-[#fff8e6] rounded-full flex items-center justify-center mb-7">
          <CheckCircle2 size={32} className="text-[#1e3a5f]" />
        </div>
        <h1 className="text-[34px] leading-tight tracking-[-1.2px] font-bold text-[#1e3a5f] mb-1">Order placed!</h1>
        {error ? (
          <p className="text-red-500 text-sm mb-4">{error}</p>
        ) : (
          <p className="text-slate-500 text-sm mb-2">
            Your order has been sent to the shop. We'll update this page as it progresses.
          </p>
        )}

        {kindnessCreditPence > 0 && (
          <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-4 text-center">
            <Heart size={24} className="text-primary mx-auto mb-2" aria-hidden />
            <p className="text-sm font-bold text-amber-800 leading-snug">
              {kindnessDonorName
                ? `${kindnessDonorName} has paid £${(kindnessCreditPence / 100).toFixed(2)} towards your order. Enjoy!`
                : `Someone has paid £${(kindnessCreditPence / 100).toFixed(2)} towards your order. Enjoy!`}
            </p>
            {/* Encourage a thank-you + spreading the word in local FB groups. */}
            <p className="text-xs text-amber-700 mt-2 leading-snug">
              Why not keep the love spreading? A wee thank-you in your local Facebook group helps more neighbours pay it forward.
            </p>
            <button
              onClick={() => {
                const text = ` Someone just paid it forward and helped cover my order on Ayrshire Hub${kindnessDonorName ? ` — thank you, ${kindnessDonorName}!` : "!"} Random acts of kindness are alive and well in Ayrshire. If you fancy spreading a little love too, it's all at Ayrshire Hub. `;
                const url = "https://www.ayrshirehub.co.uk";
                if (typeof navigator !== "undefined" && navigator.share) {
                  void navigator.share({ text, url }).catch(() => undefined);
                } else {
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener");
                }
              }}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-4 py-2 transition-colors"
            >
               Share the love
            </button>
          </div>
        )}

        {verified && orderId && !error && createdAt && estimatedMinutes && ACTIVE_STATUSES.has(status) && (
          <CountdownTimer createdAt={createdAt} estimatedMinutes={estimatedMinutes} fulfilmentType={fulfilmentType} />
        )}
        {verified && orderId && !error && (
          <StatusTracker status={status} fulfilmentType={fulfilmentType} />
        )}

        {/* Guest account registration prompt — non-blocking */}
        {isGuest && !guestDismissed && (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left">
            {guestDone ? (
              <div className="flex flex-col items-center gap-2 py-2 text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check size={20} className="text-emerald-600" strokeWidth={3} />
                </div>
                <p className="font-bold text-slate-800">Account created!</p>
                <p className="text-sm text-slate-500">Your order is saved — track it anytime in My Orders.</p>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-bold text-slate-800 text-sm">Save your order history</p>
                    <p className="text-xs text-slate-500 mt-0.5">Create a free account to track this order anytime and reorder in seconds.</p>
                  </div>
                  <button
                    type="button"
                    onClick={dismissGuestPrompt}
                    className="text-slate-400 hover:text-slate-600 mt-0.5 flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={guestEmail}
                    onChange={(e) => { setGuestEmail(e.target.value); setGuestError(null); }}
                    autoComplete="email"
                    className="text-sm"
                  />
                  <Input
                    type="password"
                    placeholder="Password (min 8 characters)"
                    value={guestPassword}
                    onChange={(e) => { setGuestPassword(e.target.value); setGuestError(null); }}
                    autoComplete="new-password"
                    onKeyDown={(e) => { if (e.key === "Enter") void claimGuestAccount(); }}
                    className="text-sm"
                  />
                  {guestError && <p className="text-xs text-red-600">{guestError}</p>}
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-sm"
                    onClick={() => void claimGuestAccount()}
                    disabled={guestSubmitting}
                  >
                    {guestSubmitting ? "Creating account…" : "Create account"}
                  </Button>
                  <button
                    type="button"
                    onClick={dismissGuestPrompt}
                    className="text-xs text-slate-400 hover:text-slate-600 text-center py-1"
                  >
                    Maybe later
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 mt-4">
          {orderId && !error ? (
            <Link href={`/order/track/${orderId}`}>
              <Button className="w-full bg-primary hover:bg-primary/90">
                <Truck size={16} className="mr-2" /> Track your order
              </Button>
            </Link>
          ) : (
            <Link href="/my-orders">
              <Button className="w-full bg-primary hover:bg-primary/90">View my orders</Button>
            </Link>
          )}
          <Link href={storeSlug ? `/order/${storeSlug}` : "/order"}>
            <Button variant="outline" className="w-full">
              <ShoppingBag size={16} className="mr-2" /> Order again
            </Button>
          </Link>
          {orderId && !error && (
            <Link
              href="/my-orders"
              className="text-sm text-slate-500 hover:text-primary transition-colors"
            >
              View all orders
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
